// (c) chaehongee. Confidential and proprietary.

import { useCallback, useRef, useEffect } from 'react'
import { useAppDispatch, useAppSelector } from '../../../store/hooks/hooks'
import { selectGenerationParams } from '../../../store/slices/generationSlice'
import { buildGenerationParamsTag } from '../utils/messageUtils'
import { useSessionRefresh } from './useSessionRefresh'
import { StreamService } from '../services/streamService'
import {
    setInput,
    appendMessage
} from '../../../store/slices/chatSlice'
import { useCanvasContext } from './useCanvasContext'
import { selectActiveSessionId, selectActiveSession } from '../../../store/selectors/sessionSelectors'
import { selectActiveChatInput, selectIsActiveChatProcessing } from '../../../store/selectors/chatSelectors'
import { selectActiveSelectedNodeIds } from '../../../store/selectors/selectionSelectors'
import { analytics } from '../../../services/analytics'

export function useChatActions() {
    const dispatch = useAppDispatch()
    const activeSessionId = useAppSelector(selectActiveSessionId)
    const session = useAppSelector(selectActiveSession)
    const selectedNodeIds = useAppSelector(selectActiveSelectedNodeIds)
    const generation = useAppSelector(selectGenerationParams)
    const input = useAppSelector(selectActiveChatInput)
    const isProcessing = useAppSelector(selectIsActiveChatProcessing)
    const { getContext } = useCanvasContext()

    const refreshSession = useSessionRefresh(activeSessionId)

    const activeStreamsRef = useRef<Record<string, { cancel: () => void }>>({})

    // Cleanup stream on unmount
    useEffect(() => {
        return () => {
            for (const handle of Object.values(activeStreamsRef.current)) {
                try { handle.cancel() } catch { }
            }
        }
    }, [])

    const canSend = Boolean(activeSessionId && session?.id && input.trim().length > 0 && !isProcessing)

    const sendMessage = useCallback(async (rawText: string) => {
        if (!session || !activeSessionId) return

        const text = rawText.trim()
        if (!text) return

        // Track message sent event
        analytics.track('message_sent', {
            message_length: text.length,
            has_selected_nodes: selectedNodeIds.length > 0,
            session_id: activeSessionId,
        })

        // 1. Prepare UI & Input
        dispatch(setInput({ sessionId: activeSessionId, input: '' }))

        dispatch(appendMessage({
            sessionId: activeSessionId,
            message: {
                kind: 'bubble',
                role: 'user',
                text,
                id: crypto.randomUUID(),
                timestamp: Date.now(),
                pending: true,
                nodeIds: selectedNodeIds.length > 0 ? [...selectedNodeIds] : undefined
            }
        }))

        activeStreamsRef.current[activeSessionId]?.cancel?.()

        // 2. Gather Context (Masks, Framed Exports, LoRA selection)
        const context = await getContext()
        const payloadSelectedNodeIds = context.selectedNodeIds
        const selectedLoraId = context.selectedLoraId

        // 3. Stream Agent Response
        const paramsTag = buildGenerationParamsTag(generation as any)
        const textWithParams = `${text}${paramsTag}`

        const handle = StreamService.start(
            dispatch,
            Object.assign(
                {
                    message: { type: 'text', text: textWithParams },
                    selected_nodes: payloadSelectedNodeIds,
                    session_id: session.id,
                },
                selectedLoraId ? { selected_lora_id: selectedLoraId as any } : {}
            ) as any,
            refreshSession
        )
        activeStreamsRef.current[activeSessionId] = handle
    }, [
        session,
        activeSessionId,
        selectedNodeIds,
        getContext,
        dispatch,
        generation,
        refreshSession
    ])

    const handleSend = useCallback(async () => {
        if (!canSend) return
        await sendMessage(input)
    }, [canSend, sendMessage, input])

    const handleSendWithText = useCallback(async (text: string) => {
        await sendMessage(text)
    }, [sendMessage])

    return {
        handleSend,
        handleSendWithText,
        canSend
    }
}
